<template>
  <div class="height-100-p">
    <el-form :model="editForm" :rules="editFormRules" ref="editForm">
      <el-table
        :data="layoutTemplate.list"
        v-loading="layoutTemplate.loadingSearch"
        id="layoutTemplate-data-list"
        size="mini"
        tooltip-effect="dark"
        class="dark-blue-table"
        :empty-text="$t('common.notAbleToFindRecords')"
        stripe
      >
        <el-table-column
          prop="day"
          :label="$t('layoutTemplate.dayOfWeek')"
          :min-width="5"
          align="center"
        >
          <template slot-scope="scope">
            <template v-if="scope.row.edit">
              <el-form-item prop="dayOfWeek">
                <el-select
                  v-model="editForm.dayName"
                  id="editForm-dayOfWeek"
                  ref="filter-day"
                  @change="handleChange"
                >
                  <el-option
                  v-for="item in dayOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                  ></el-option>
                </el-select>
              </el-form-item>
            </template>
            <template v-else>
              <span>{{ scope.row.dayName }}</span>
            </template>
          </template>
        </el-table-column>
        <el-table-column prop="code" :label="$t('layoutTemplate.code')" :min-width="5" align="center">
          <template slot-scope="scope">
            <template v-if="scope.row.edit">
              <el-form-item prop="code">
                <el-input v-model="editForm.code" v-numeric-validation maxlength="10"></el-input>
              </el-form-item>
            </template>
            <template v-else>
              <span>{{ scope.row.code }}</span>
            </template>
          </template>
        </el-table-column>

        <el-table-column prop="name" :label="$t('layoutTemplate.name')" :min-width="5" align="center">
          <template slot-scope="scope">
            <template v-if="scope.row.edit">
              <el-form-item prop="name">
                <el-input v-model="editForm.name" maxlength="50"></el-input>
              </el-form-item>
            </template>
            <template v-else>
              <span>{{ scope.row.name }}</span>
            </template>
          </template>
        </el-table-column>
        <el-table-column
          prop="positions"
          :label="$t('layoutTemplate.positionsAssigned')"
          :min-width="5"
          align="center"
        >
          <template slot-scope="scope">
            <span>{{ scope.row.positionsAssigned }}</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="defaultTemplate"
          :label="$t('layoutTemplate.defaultTemplate')"
          :min-width="5"
          align="center">
          <template slot-scope="scope">
            <template v-if="scope.row.edit">
              <el-form-item prop="defaultTemplate">
                <el-select v-model="editForm.defaultTemplate" class="inline-input" size="mini">
                  <el-option :label="yes.label" :value="yes.value"></el-option>
                  <el-option :label="no.label" :value="no.value"></el-option>
                </el-select>
              </el-form-item>
            </template>
            <template v-else>
              <i v-if="scope.row.defaultTemplate" class="el-icon-success green-success"></i>
            </template>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.options')" :min-width="4" align="center">
          <template slot-scope="scope">
            <el-tooltip
              v-if="showEditButton(scope.row)"
              class="item"
              effect="dark"
              :enterable="false"
              :content="$t('warehouse.orderEntry.edit')"
              placement="top-start"
            >
              <i class="el-icon-edit icon-action" @click="handleEdit(scope.row)"></i>
            </el-tooltip>
            <template v-else-if="scope.row.edit">
              <el-button icon="el-icon-success" circle size="mini" @click="handleUpdate(scope.row)"></el-button>
              <el-button icon="el-icon-circle-close" circle size="mini" @click="cancelEdit(scope.row)"></el-button>
            </template>
            <template v-if="!scope.row.edit">
              <el-tooltip
                v-if="showEditButton(scope.row)"
                class="item"
                effect="dark"
                :enterable="false"
                :content="$t('warehouse.orderEntry.copy')"
                placement="top-start"
              >
                <copy-dialog
                  :layoutTemplateId="scope.row.id"
                  :dayOfWeek="scope.row.dayName"
                  :code="scope.row.code"
                  :name="scope.row.name"
                />
              </el-tooltip>
              <view-details
                :layoutTemplateId="scope.row.id"
                :dayOfWeek="scope.row.dayName"
                :code="scope.row.code"
                :name="scope.row.name"
              />
              <el-tooltip
                  v-if="showDeleteButton(scope.row)"
                  class="item"
                  effect="dark"
                  :enterable="false"
                  :content="$t('common.delete')"
                  placement="top-start"
              >
                <i class="el-icon-delete icon-action" @click="handleDelete(scope.row)"></i>
              </el-tooltip>
            </template>
          </template>
        </el-table-column>
      </el-table>
    </el-form>
    <div class="pagination-footer" v-show="layoutTemplate.total > 0">
      <span class="el-pagination__total">
        <span class="el-pagination__title">{{ $t('layoutTemplate.totalLayouts') }}</span>
        {{ layoutTemplate.total }}
      </span>
      <el-pagination
        @size-change="handleSearchChangeLimit"
        :page-sizes="sizes"
        :page-size="layoutTemplate.actualFilters.rows"
        :current-page.sync="layoutTemplate.actualFilters.page"
        @current-change="handleSearchChangePage"
        :total="layoutTemplate.total"
        layout="sizes, prev, pager, next, jumper"
      ></el-pagination>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import { Message } from 'element-ui';
import moment from 'moment';
import constants from '@/utils/constants';
import { LAYOUT_TEMPLATE_SEARCH, LAYOUT_TEMPLATE_SET_EDITING_ROW, LAYOUT_TEMPLATE_UPDATE } from '@/store/actions';
import ViewDetails from './components/ViewDetails';
import CopyDialog from './components/CopyDialog';

export default {
  name: 'SearchSummary',
  components: {
    CopyDialog,
    ViewDetails
  },
  computed: {
    ...mapGetters(['layoutTemplate']),
    dayOptions() {
      return constants.DAYS_OPTIONS.filter(option => option.value != null);
    }
  },
  data() {
    return {
      editForm: {
        id: null,
        warehouseId: null,
        day: null,
        dayName: null,
        code: null,
        defaultTemplate: null,
        name: null
      },
      editFormRules: {
        code: [
          {
            required: true,
            message: this.$t('layoutTemplate.error.codeRequired'),
            trigger: 'blur'
          }
        ],
        name: [
          {
            required: true,
            message: this.$t('layoutTemplate.error.nameRequired'),
            trigger: 'blur'
          }
        ]
      },
      yes: constants.STATUS_BOOLEAN.yes,
      no: constants.STATUS_BOOLEAN.no,
      sizes: constants.TABLES.DEFAULT_LIMIT_SIZES
    };
  },
  methods: {
    handleSearchChangePage(val) {
      this.$store.dispatch(LAYOUT_TEMPLATE_SEARCH, { page: val });
    },
    handleSearchChangeLimit(val) {
      this.$store.dispatch(LAYOUT_TEMPLATE_SEARCH, {
        rows: val,
        page: constants.TABLES.DEFAULT_PAGE
      });
    },
    handleEdit(row) {
      this.editForm.id = row.id;
      this.editForm.warehouseId = row.warehouseId;
      this.editForm.day = row.day;
      this.editForm.dayName = row.dayName;
      this.editForm.code = row.code;
      this.editForm.name = row.name;
      this.editForm.defaultTemplate = row.defaultTemplate;
      this.$store.dispatch(LAYOUT_TEMPLATE_SET_EDITING_ROW, true);
      row.edit = true;
    },
    handleChange(id) {
      this.editForm.day = id;
    },
    cancelEdit(row) {
      this.$store.dispatch(LAYOUT_TEMPLATE_SET_EDITING_ROW, false);
      row.edit = false;
    },
    handleUpdate() {
      this.$refs.editForm.validate((valid, validationsErrors) => {
        if (valid) {
          Message.closeAll();
          const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
          this.$store
            .dispatch(LAYOUT_TEMPLATE_UPDATE, { layoutToUpdate: this.editForm, layoutTemplateId: this.editForm.id })
            .then((response) => {
              loading.close();
              this.$store.dispatch(LAYOUT_TEMPLATE_SEARCH, {});
              this.$store.dispatch(LAYOUT_TEMPLATE_SET_EDITING_ROW, false);
              this.$message.success(response.message);
            })
            .catch(() => {
              loading.close();
            });
        } else {
          let errorMessage = '';
          Object.keys(validationsErrors).forEach((key) => {
            validationsErrors[key].forEach((validation) => {
              errorMessage += `${validation.message}. `;
            });
          });
          this.$message.warning(errorMessage);
        }
      });
    },
    showEditButton(row) {
      return (
        this.$can(this.permissions.WAREHOUSE.EDIT_LAYOUT_TEMPLATES)
        && !this.layoutTemplate.isEditing
        && !row.edit
      );
    },
    showDeleteButton(row) {
      return !(row.activeForReceiving || row.activeForOutbound)
          && this.$can(this.permissions.WAREHOUSE.DELETE_LAYOUTS);
    },
    handleDelete(row) {
      const message = this.$t('layout.deleteTemplateConfirmMessage')
        .replace('[date]', moment(row.layoutDate).format(this.dateDisplayFormat))
        .replace('[dayOfWeek]', row.dayName)
        .replace('[templateSize]', row.code)
        .replace('[templateName]', row.name)
        .replace('[positionsAssigned]', row.positionsAssigned);
      this.$confirm(message, { dangerouslyUseHTMLString: true, confirmButtonText: this.$t('common.delete') })
        .then(() => {
          this.deletePosition(row);
        }).catch(() => {});
    },
    deletePosition(row) {
      const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
      this.$store.dispatch('layoutTemplate/delete', row.id).then((response) => {
        this.$store.dispatch('layoutTemplate/search', {});
        loading.close();
        this.$message.success(response.message);
      })
        .catch(() => {
          loading.close();
        });
    },
  }
};
</script>
